import React, { useState, useCallback, useMemo } from 'react'
import AsyncSelect from 'react-select/async'
// import axios from 'axios'
import { Urls } from '../../../Urls'
import { toast } from 'react-toastify'
import axios from '../../../Axios'

const AutoComplete = ({
  value,
  onChange,
  onSelect,
  isDisabled = false,

  // API config
  searchFields = 'new_locations',
  limit = 20,
  minLength = 3,
  placeholder = '',
  showNoSuggestions = true,
  enableToast = false,
  module_key,
  user_identifier,
  className = '',
  classNamePrefix = '',
  customStyles = {},
  groupLabelStyles = {},
  isClearable = true,
  hideInitialMessage = false,
}) => {
  const [noOptions, setNoOptions] = useState(false)

  // ===== MEMOIZED DEBOUNCE FUNCTION =====
  const debouncePromise = useMemo(
    () => {
      let timeout
      return (func, delay) => {
        return (...args) => {
          clearTimeout(timeout)
          return new Promise((resolve) => {
            timeout = setTimeout(() => {
              resolve(func(...args))
            }, delay)
          })
        }
      }
    },
    []
  )

  // ===== CUSTOM COMPONENTS =====
  const customComponents = useMemo(
    () => ({
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      LoadingIndicator: () => (
        <div style={{ paddingRight: '8px' }}>
          <div className="custom-spinner" />
        </div>
      ),
    }),
    []
  )

  // ===== BUILD API URL =====
  const buildApiUrl = useCallback(
    (inputValue) => {
      const baseUrl = `${Urls.Autocomplete.Get.getlist}?suggest=${inputValue}&limit=${limit}&searchFields=${searchFields}`
      return module_key
        ? `${baseUrl}&module_key=${module_key}&user_identifier=${user_identifier}`
        : baseUrl
    },
    [limit, searchFields, module_key, user_identifier]
  )

  // ===== TRANSFORM DATA INTO OPTIONS =====
  const transformToOptions = useCallback((data, hasModuleKey) => {
    if (!hasModuleKey) {
      return data.map((item) => ({
        label: item.location_name,
        value: item.location_name,
        full: item,
      }))
    }

    const myLocations = []
    const otherLocations = []

    data.forEach((item) => {
      const option = {
        label: item.location_name,
        value: item.location_name,
        full: item,
        type: item.type || null,
      }

      if (item.type) {
        myLocations.push(option)
      } else {
        otherLocations.push(option)
      }
    })

    // No "My Locations" → flat list, no group headers at all
    if (myLocations.length === 0) {
      return otherLocations
    }

    // Has "My Locations" → grouped with both headers
    const groups = [{ label: 'My Locations', options: myLocations }]

    if (otherLocations.length > 0) {
      groups.push({ label: 'Other Locations', options: otherLocations })
    }

    return groups
  }, [])

  // ===== FETCH OPTIONS =====
  const fetchOptions = useCallback(
    async (inputValue) => {
      if (!inputValue || inputValue.length < minLength) return []

      try {
        const url = buildApiUrl(inputValue)
        const res = await axios.get(url)
        const data = res.data.value || []

        setNoOptions(data.length === 0)

        return transformToOptions(data, !!module_key)
      } catch (error) {
        console.error('AutoComplete fetch error:', error)
        return []
      }
    },
    [minLength, buildApiUrl, transformToOptions, module_key]
  )

  // ===== LOAD OPTIONS WITH DEBOUNCE =====
  const loadOptions = useMemo(
    () => debouncePromise(fetchOptions, 500),
    [debouncePromise, fetchOptions]
  )

  // ===== GET ICON CONFIG =====
  const getIconConfig = useCallback((type) => {
    const iconMap = {
      warehouse: 'fas fa-warehouse',
      plant: 'fa fa-industry',
      destination: 'fa fa-map-marker',
    }

    const iconClass = type ? iconMap[type.toLowerCase()] || '' : ''
    const iconColor = type ? '#290767' : '#999'

    return { iconClass, iconColor }
  }, [])

  // ===== FORMAT OPTION LABEL =====
  const formatOptionLabel = useCallback(
    (option) => {
      const { iconClass, iconColor } = getIconConfig(option.type)
      const fullText = option.label || ''
      const firstCommaIndex = fullText.indexOf(',')

      let mainTitle = fullText
      let subAddress = ''

      if (firstCommaIndex > 0 && firstCommaIndex < fullText.length - 1) {
        mainTitle = fullText.substring(0, firstCommaIndex).trim()
        subAddress = fullText.substring(firstCommaIndex + 1).trim()
      }

      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%', overflow: 'hidden' }}>
          <i
            className={iconClass || 'fa fa-map-marker-alt'}
            style={{
              color: iconColor || '#393185',
              fontSize: '12px',
              marginTop: '3px',
              flexShrink: 0,
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <span
              style={{
                fontSize: '12.5px',
                fontWeight: '600',
                lineHeight: '1.35',
                color: 'inherit',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={mainTitle}
            >
              {mainTitle}
            </span>
            {subAddress ? (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '400',
                  color: '#64748B',
                  lineHeight: '1.3',
                  marginTop: '1px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={subAddress}
              >
                {subAddress}
              </span>
            ) : null}
          </div>
        </div>
      )
    },
    [getIconConfig]
  )

  // ===== FORMAT GROUP LABEL =====
  const formatGroupLabel = useCallback(
    (group) => {
      const defaultGroupStyles = {
        fontWeight: '600',
        fontSize: '12px',
        color: '#4f4f4f',
      }

      // Allow per-group customization
      const customGroupStyles =
        group.label === 'My Locations'
          ? groupLabelStyles.myLocations || {}
          : groupLabelStyles.otherLocations || {}

      return (
        <div
          style={{
            ...defaultGroupStyles,
            ...customGroupStyles,
          }}
        >
          {group.label}
        </div>
      )
    },
    [groupLabelStyles]
  )

  // ===== HANDLE CHANGE =====
  const handleChange = useCallback(
    (selected) => {
      if (!selected) {
        onChange('')
        onSelect(null)
        return
      }

      if (enableToast && selected.full?.title) {
        toast.error('Please select location from dropdown')
        return
      }

      onChange(selected.value)
      onSelect(selected.full)
    },
    [onChange, onSelect, enableToast]
  )

  // ===== NO OPTIONS MESSAGE =====
  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (!inputValue || inputValue.length < minLength) {
        return hideInitialMessage ? null : 'Type to search locations'
      }
      return showNoSuggestions ? 'No location found' : null
    },
    [minLength, showNoSuggestions, hideInitialMessage]
  )

  // ===== DEFAULT STYLES =====
  const selectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        borderRadius: 4,
        minHeight: 36,
        fontSize: '14px',
        fontFamily: 'inherit',
        fontWeight: '400',
        color: '#495057',
        boxShadow: 'none',
        borderColor: '#ccc',
        cursor: 'text',
      }),
      singleValue: (base) => ({
        ...base,
        fontSize: '14px',
        fontFamily: 'inherit',
        fontWeight: '400',
        color: '#495057',
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: '14px',
        fontFamily: 'inherit',
        color: '#495057',
        cursor: 'text',
      }),
      valueContainer: (base) => ({
        ...base,
        padding: '2px 8px',
        cursor: 'text',
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.05)',
        border: '1px solid #E2E8F0',
        padding: '4px',
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
        left: 0,
        right: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }),
      menuList: (base) => ({
        ...base,
        maxHeight: '260px',
        padding: '0',
        '&::-webkit-scrollbar': {
          width: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#CBD5E1',
          borderRadius: '3px',
        },
      }),
      option: (base, state) => ({
        ...base,
        fontSize: '12.5px',
        padding: '7px 10px',
        margin: '2px 0',
        borderRadius: '6px',
        cursor: 'pointer',
        backgroundColor: state.isSelected
          ? '#393185'
          : state.isFocused
          ? '#F0F4FF'
          : 'transparent',
        color: state.isSelected
          ? '#ffffff'
          : state.isFocused
          ? '#1E1B4B'
          : '#2B2B2B',
        fontWeight: state.isSelected || state.isFocused ? '600' : '400',
        transition: 'background-color 0.15s ease, color 0.15s ease',
      }),
      groupHeading: (base) => ({
        ...base,
        fontSize: '11px',
        fontWeight: '700',
        color: '#5C6672',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        padding: '6px 8px 3px 8px',
        margin: 0,
      }),
      placeholder: (base, state) => ({
        ...base,
        fontSize: '14px',
        fontFamily: 'inherit',
        fontWeight: '400',
        color: '#8A93A0',
        display: state.isFocused ? 'none' : 'block',
      }),
      ...customStyles,
    }),
    [customStyles]
  )

  return (
    <AsyncSelect
      cacheOptions
      defaultOptions
      loadOptions={loadOptions}
      components={customComponents}
      isDisabled={isDisabled}
      value={value}
      className={className}
      classNamePrefix={classNamePrefix}
      placeholder={placeholder}
      styles={selectStyles}
      formatOptionLabel={formatOptionLabel}
      formatGroupLabel={formatGroupLabel}
      onChange={handleChange}
      noOptionsMessage={noOptionsMessage}
      isClearable={isClearable}
    />
  )
}

export default AutoComplete